/**
 * Sample Selection Modal
 *
 * Supports:
 * - Dynamically rendered Shopify cart drawers
 * - Full cart pages
 * - Cart attributes saved through /cart/update.js
 * - Synchronisation between drawer and cart-page modals
 * - Live order-note confirmation text
 * - Live order-note character countdown
 *
 * Cross-browser notes:
 * - Handles clicks landing on text nodes.
 * - Supports older browsers without NodeList.forEach.
 * - Supports browsers without the CustomEvent constructor.
 * - Uses capture-phase delegated clicks so Shopify cart drawer
 *   replacement does not break the sample modal button.
 */
(function () {
  'use strict';

  var MAX_SAMPLES = 2;
  var DEFAULT_EMPTY_TEXT = 'Choose your complimentary samples';

  /* ---------------------------------------------------------
   * Compatibility helpers
   * --------------------------------------------------------- */

  if (
    window.NodeList &&
    !NodeList.prototype.forEach
  ) {
    NodeList.prototype.forEach = Array.prototype.forEach;
  }

  (function ensureCustomEvent() {
    if (typeof window.CustomEvent === 'function') return;

    function CustomEventPolyfill(eventName, params) {
      params =
        params || {
          bubbles: false,
          cancelable: false,
          detail: null
        };

      var evt = document.createEvent('CustomEvent');

      evt.initCustomEvent(
        eventName,
        params.bubbles,
        params.cancelable,
        params.detail
      );

      return evt;
    }

    CustomEventPolyfill.prototype = window.Event
      ? window.Event.prototype
      : {};

    window.CustomEvent = CustomEventPolyfill;
  })();

  /**
   * Finds the nearest matching element, including when the click
   * target is a text node rather than an HTML element.
   */
  function safeClosest(node, selector) {
    while (node && node.nodeType !== 1) {
      node = node.parentNode;
    }

    if (!node) return null;

    if (typeof node.closest === 'function') {
      return node.closest(selector);
    }

    while (node) {
      if (
        node.matches &&
        node.matches(selector)
      ) {
        return node;
      }

      node = node.parentElement;
    }

    return null;
  }

  /**
   * Returns the Shopify-aware cart update URL.
   */
  function getCartUpdateUrl() {
    if (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) {
      return window.Shopify.routes.root + 'cart/update.js';
    }

    return '/cart/update.js';
  }

  /**
   * Posts JSON to Shopify.
   */
  function postJson(url, body) {
    if (typeof window.fetch === 'function') {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      }).then(function (response) {
        if (!response.ok) {
          throw new Error(
            'Cart update failed with status ' + response.status
          );
        }

        return response.json();
      });
    }

    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();

      xhr.open('POST', url, true);
      xhr.withCredentials = true;
      xhr.setRequestHeader(
        'Content-Type',
        'application/json'
      );
      xhr.setRequestHeader(
        'Accept',
        'application/json'
      );

      xhr.onload = function () {
        if (
          xhr.status >= 200 &&
          xhr.status < 300
        ) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (parseError) {
            resolve({});
          }
        } else {
          reject(
            new Error(
              'Cart update failed with status ' +
                xhr.status
            )
          );
        }
      };

      xhr.onerror = function () {
        reject(
          new Error(
            'Cart update failed due to a network error'
          )
        );
      };

      xhr.send(JSON.stringify(body));
    });
  }

  /**
   * Opens a sample-selection modal.
   */
  function openModal(modal) {
    if (!modal) return;

    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sample-modal-open');

    var firstCheckbox = modal.querySelector(
      '[data-sample-checkbox]'
    );

    if (firstCheckbox) {
      firstCheckbox.focus();
    }
  }

  /**
   * Closes a sample-selection modal.
   */
  function closeModal(modal) {
    if (!modal) return;

    modal.setAttribute('aria-hidden', 'true');

    if (
      !document.querySelector(
        '.SampleModal[aria-hidden="false"]'
      )
    ) {
      document.body.classList.remove(
        'sample-modal-open'
      );
    }
  }

  /**
   * Updates every sample summary.
   */
  function updateSummaryText(selectedValues) {
    document
      .querySelectorAll('[data-sample-summary]')
      .forEach(function (element) {
        if (selectedValues.length) {
          element.textContent =
            'Samples: ' +
            selectedValues.join(', ');

          return;
        }

        element.textContent =
          element.getAttribute(
            'data-sample-empty-label'
          ) || DEFAULT_EMPTY_TEXT;
      });
  }

  /**
   * Synchronises selected samples across all modals.
   */
  function syncModalCheckboxes(selectedValues) {
    document
      .querySelectorAll('[data-sample-checkbox]')
      .forEach(function (checkbox) {
        checkbox.checked =
          selectedValues.indexOf(
            checkbox.value
          ) !== -1;
      });
  }

  /**
   * Returns every element sharing the given ID.
   */
  function getAllNodesWithId(id) {
    if (!id) return [];

    var escapedId =
      window.CSS &&
      typeof CSS.escape === 'function'
        ? CSS.escape(id)
        : id;

    try {
      return Array.prototype.slice.call(
        document.querySelectorAll(
          '[id="' + escapedId + '"]'
        )
      );
    } catch (selectorError) {
      return Array.prototype.slice
        .call(
          document.querySelectorAll(
            '.SampleModal'
          )
        )
        .filter(function (node) {
          return node.id === id;
        });
    }
  }

  /**
   * Removes stale duplicate modals.
   */
  function removeDuplicateModal(modal) {
    if (!modal.id) return;

    getAllNodesWithId(modal.id).forEach(
      function (node) {
        if (node !== modal) {
          node.remove();
        }
      }
    );
  }

  /**
   * Finds the working modal for the given ID.
   */
  function findCanonicalModal(id) {
    var candidates = getAllNodesWithId(id);

    if (!candidates.length) return null;

    var initialised = candidates.filter(
      function (node) {
        return (
          node.dataset.sampleModalInit ===
          'true'
        );
      }
    );

    return initialised[0] || candidates[0];
  }

  /**
   * Updates the order-note character countdown.
   */
  function updateNoteCounter(noteField) {
    if (!noteField) return;

    var maximumLength = parseInt(
      noteField.getAttribute('maxlength'),
      10
    );

    if (!maximumLength) return;

    var parent = noteField.parentElement;

    if (!parent) return;

    var counter = parent.querySelector(
      '[data-note-counter]'
    );

    if (!counter) return;

    var remaining = Math.max(
      maximumLength - noteField.value.length,
      0
    );

    counter.textContent =
      remaining +
      (remaining === 1
        ? ' character remaining'
        : ' characters remaining');

    counter.classList.toggle(
      'is-near-limit',
      remaining <= 30
    );
  }

  /**
   * Initialises all note counters inside a root.
   */
  function initNoteCounters(root) {
    if (!root) return;

    if (
      root.matches &&
      root.matches('[data-note-field]')
    ) {
      updateNoteCounter(root);
    }

    if (
      typeof root.querySelectorAll !==
      'function'
    ) {
      return;
    }

    root
      .querySelectorAll('[data-note-field]')
      .forEach(function (noteField) {
        updateNoteCounter(noteField);
      });
  }

  /**
   * Initialises a sample modal.
   */
  function initSampleModal(modal) {
    if (
      !modal ||
      modal.dataset.sampleModalInit ===
        'true'
    ) {
      return;
    }

    removeDuplicateModal(modal);

    modal.dataset.sampleModalInit = 'true';

    /*
     * Move the modal to the body to prevent cart-drawer
     * overflow and stacking issues.
     */
    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }
  }

  /**
   * Confirms and saves the selected samples.
   */
  function confirmSampleSelection(
    modal,
    confirmButton
  ) {
    var checkedCheckboxes =
      Array.prototype.slice.call(
        modal.querySelectorAll(
          '[data-sample-checkbox]:checked'
        )
      );

    var selectedValues =
      checkedCheckboxes.map(
        function (checkbox) {
          return checkbox.value;
        }
      );

    var attributes = {
      'Sample Choice 1':
        selectedValues[0] || '',
      'Sample Choice 2':
        selectedValues[1] || ''
    };

    confirmButton.disabled = true;
    confirmButton.setAttribute(
      'aria-busy',
      'true'
    );

    postJson(
      getCartUpdateUrl(),
      {
        attributes: attributes
      }
    )
      .then(function () {
        updateSummaryText(selectedValues);
        syncModalCheckboxes(selectedValues);
        closeModal(modal);

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: {
              attributes: attributes,
              selectedSamples: selectedValues
            }
          })
        );
      })
      .catch(function (error) {
        console.error(
          'Error updating sample cart attributes:',
          error
        );

        alert(
          'Sorry, something went wrong saving your sample selection. Please try again.'
        );
      })
      .then(function () {
        confirmButton.disabled = false;
        confirmButton.removeAttribute(
          'aria-busy'
        );
      });
  }

  /**
   * Initialises all sample modals inside a root.
   */
  function initAll(root) {
    if (!root) return;

    if (
      root.classList &&
      root.classList.contains('SampleModal')
    ) {
      initSampleModal(root);
    }

    if (
      typeof root.querySelectorAll !==
      'function'
    ) {
      return;
    }

    root
      .querySelectorAll('.SampleModal')
      .forEach(function (modal) {
        initSampleModal(modal);
      });
  }

  /**
   * Opens and closes sample modals.
   *
   * Capture phase is used so this continues working after
   * Shopify completely replaces the cart drawer.
   */
  document.addEventListener(
    'click',
    function (event) {
      var openButton = safeClosest(
        event.target,
        '[data-sample-modal-open]'
      );

      if (openButton) {
        event.preventDefault();

        var modalId =
          openButton.getAttribute(
            'data-sample-modal-open'
          );

        if (!modalId) return;

        /*
         * Supports values with or without a leading #.
         */
        modalId = modalId.replace(/^#/, '');

        /*
         * First look for the modal inside the newly rendered
         * cart drawer.
         */
        var cartContainer = safeClosest(
          openButton,
          '#sidebar-cart, cart-drawer, .CartDrawer, [data-cart-drawer]'
        );

        var modal = null;

        if (cartContainer) {
          try {
            modal =
              cartContainer.querySelector(
                '[id="' + modalId + '"]'
              );
          } catch (error) {
            modal = null;
          }
        }

        /*
         * Fall back to the existing modal already moved
         * into document.body.
         */
        if (!modal) {
          modal =
            findCanonicalModal(modalId);
        }

        if (!modal) {
          console.warn(
            'Sample modal not found for ID:',
            modalId
          );

          return;
        }

        /*
         * Initialise immediately rather than relying only
         * on MutationObserver timing.
         */
        initSampleModal(modal);

        /*
         * Resolve the surviving modal again in case duplicate
         * removal changed which modal remains in the DOM.
         */
        modal =
          findCanonicalModal(modalId) ||
          modal;

        openModal(modal);

        return;
      }

      var closeElement = safeClosest(
        event.target,
        '[data-sample-modal-close]'
      );

      if (closeElement) {
        event.preventDefault();

        closeModal(
          safeClosest(
            closeElement,
            '.SampleModal'
          )
        );
      }
    },
    true
  );

  /**
   * Enforces the maximum sample limit.
   */
  document.addEventListener(
    'change',
    function (event) {
      var checkbox = safeClosest(
        event.target,
        '[data-sample-checkbox]'
      );

      if (!checkbox) return;

      var modal = safeClosest(
        checkbox,
        '.SampleModal'
      );

      if (!modal) return;

      var checked =
        modal.querySelectorAll(
          '[data-sample-checkbox]:checked'
        );

      if (checked.length > MAX_SAMPLES) {
        checkbox.checked = false;

        alert(
          'You can only select up to ' +
            MAX_SAMPLES +
            ' samples.'
        );
      }
    }
  );

  /**
   * Handles the confirm-selection button.
   */
  document.addEventListener(
    'click',
    function (event) {
      var confirmButton = safeClosest(
        event.target,
        '[data-sample-confirm]'
      );

      if (!confirmButton) return;

      event.preventDefault();

      var modal = safeClosest(
        confirmButton,
        '.SampleModal'
      );

      if (!modal) return;

      confirmSampleSelection(
        modal,
        confirmButton
      );
    },
    true
  );

  /**
   * Updates the order-note confirmation label.
   */
  document.addEventListener(
    'click',
    function (event) {
      var saveButton = safeClosest(
        event.target,
        '[data-note-save]'
      );

      if (!saveButton) return;

      var noteField =
        document.querySelector(
          '#sidebar-cart #cart-note'
        );

      if (!noteField) return;

      var hasNote =
        noteField.value.trim().length > 0;

      document
        .querySelectorAll(
          '#sidebar-cart [data-note-status]'
        )
        .forEach(function (element) {
          var defaultLabel =
            element.getAttribute(
              'data-note-default'
            ) ||
            'Add a note with your order';

          var addedLabel =
            element.getAttribute(
              'data-note-added'
            ) ||
            '✓ Order note added';

          element.textContent = hasNote
            ? addedLabel
            : defaultLabel;
        });

      updateNoteCounter(noteField);
    }
  );

  /**
   * Updates the note character countdown while typing.
   */
  document.addEventListener(
    'input',
    function (event) {
      var noteField = safeClosest(
        event.target,
        '[data-note-field]'
      );

      if (!noteField) return;

      updateNoteCounter(noteField);
    }
  );

  /**
   * Closes an open modal with Escape.
   */
  document.addEventListener(
    'keydown',
    function (event) {
      if (
        event.key !== 'Escape' &&
        event.keyCode !== 27
      ) {
        return;
      }

      var openModalElement =
        document.querySelector(
          '.SampleModal[aria-hidden="false"]'
        );

      closeModal(openModalElement);
    }
  );

  /**
   * Initial page load.
   */
  document.addEventListener(
    'DOMContentLoaded',
    function () {
      initAll(document);
      initNoteCounters(document);
    }
  );

  /**
   * Shopify theme editor section reload.
   */
  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initAll(event.target);
      initNoteCounters(event.target);
    }
  );

  /**
   * Watches for dynamically added cart-drawer markup.
   */
  var observer = new MutationObserver(
    function (mutations) {
      mutations.forEach(
        function (mutation) {
          Array.prototype.forEach.call(
            mutation.addedNodes,
            function (node) {
              if (
                !node ||
                node.nodeType !== 1
              ) {
                return;
              }

              initAll(node);
              initNoteCounters(node);
            }
          );
        }
      );
    }
  );

  function startObserver() {
    if (!document.body) return;

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      startObserver
    );
  } else {
    initAll(document);
    initNoteCounters(document);
    startObserver();
  }
})();